import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, Fingerprint } from "lucide-react";
import { passkeySupported, passkeyLogin } from "@/lib/passkey";

const DEMO = [
  { label: "Bureau Admin", email: "daporter37@gmail.com", password: "Bureau@2026" },
  { label: "HR Manager", email: "hr@acme.com", password: "Hr@2026" },
  { label: "Employee", email: "employee@acme.com", password: "Emp@2026" },
];
export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("daporter37@gmail.com");
  const [password, setPassword] = useState("Bureau@2026");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pkBusy, setPkBusy] = useState(false);
  const { setUser } = useAuth();
  const pkLogin = async () => {
    setError(""); setPkBusy(true);
    try { const data = await passkeyLogin(email.trim() || null); setUser(data); navigate("/dashboard"); }
    catch (e) { setError(e.response?.data?.detail || (e.name === "NotAllowedError" ? "Biometric prompt was cancelled or no passkey is saved on this device." : e.message || "Passkey sign-in failed")); }
    finally { setPkBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-slate-950 text-white overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1631193816258-28b44b21e78b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="workspace"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="relative flex items-center gap-2.5">
          <div className="bg-white rounded-2xl px-5 py-3">
            <img src="/rightpay-logo.png" alt="RightPay" className="h-20 sm:h-24 w-auto object-contain" />
          </div>
        </div>
        <div className="relative space-y-4 max-w-md">
          <h1 className="font-display text-4xl font-bold tracking-tight leading-tight">
            Multi-client payroll, run with precision.
          </h1>
          <p className="text-slate-300 leading-relaxed">
            Manage every client's employees, payroll runs, taxes, timesheets and leave from one
            command center — with AI keeping an eye on the numbers.
          </p>
          <div className="flex gap-8 pt-4 font-mono-num">
            <div><div className="text-3xl font-bold">4+</div><div className="text-xs text-slate-400 uppercase tracking-wider">Clients</div></div>
            <div><div className="text-3xl font-bold">24+</div><div className="text-xs text-slate-400 uppercase tracking-wider">Employees</div></div>
            <div><div className="text-3xl font-bold">FICA</div><div className="text-xs text-slate-400 uppercase tracking-wider">Tax Ready</div></div>
          </div>
        </div>
        <div className="relative text-xs text-slate-500">© 2026 RightPay Bureau</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="bg-white rounded-xl px-3 py-2 border border-border">
              <img src="/rightpay-logo.png" alt="RightPay" className="h-16 w-auto object-contain" />
            </div>
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Welcome back</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Sign in to your bureau workspace.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
            <div className="flex justify-between text-xs">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-primary hover:underline">Forgot password?</Link>
              <Link to="/forgot-password?mode=username" data-testid="forgot-username-link" className="text-muted-foreground hover:underline">Forgot username?</Link>
            </div>
            <Button data-testid="login-submit-button" type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sign in
            </Button>
            {passkeySupported() && (
              <Button type="button" variant="outline" className="w-full" data-testid="login-passkey-button" disabled={pkBusy} onClick={pkLogin}>
                {pkBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Fingerprint className="w-4 h-4 mr-2" />} Sign in with Face ID / fingerprint
              </Button>
            )}
          </form>

          <div className="mt-6 pt-5 border-t border-border">
            <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Demo accounts</p>
            <div className="grid gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  data-testid={`demo-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => { setEmail(d.email); setPassword(d.password); }}
                  className="text-left px-3 py-2 rounded-lg border border-border hover:bg-accent transition-colors text-sm flex justify-between items-center"
                >
                  <span className="font-medium">{d.label}</span>
                  <span className="text-xs text-muted-foreground font-mono-num">{d.email}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
